<template>
  <div class="grow p-3 flex flex-col">
    <div class="flex flex-row flex-nowrap w-full border-b mb-2 relative">
      <FormKit
        v-model="newPresetName"
        label="Nieuw sjabloon"
        :ignore="true"
        type="text"
        placeholder="Naam sjabloon..."
        suffix-icon="add"
        @suffix-icon-click="saveCurrentStateAsPreset"
        @keydown.enter.prevent="saveCurrentStateAsPreset"
      />
    </div>
    <div class="mb-2">
      <AppButton icon="fa-paste" size="sm" variant="outline" @click="pastePreset">
        {{ $t("common.messages.Paste profile") }}
      </AppButton>
    </div>
    <span class="font-bold text-sm">Opgeslagen profielen</span>
    <ul class="list-none">
      <li
        v-for="preset in presets"
        :key="preset.id"
        class="flex flex-row items-center border-2 mt-1 rounded hover:border-primary"
        :class="{ 'border-primary': isEditing(preset) }"
      >
        <div v-if="!isEditing(preset)" class="flex">
          <button type="button"
                  :id="`preset__${preset.id}`"
                  class="p-3 block cursor-pointer grow bg-transparent focus:ring-0 focus:outline-none truncate w-44 text-left"
                  @click="applyPreset(preset)"
          >
            {{ preset.name }}
          </button>
          <div class="float-right">
            <AppIcon
              v-tooltip="$t('common.messages.Copy profile')"
              icon="fa-copy"
              class="ml-auto hover:text-primary icon active:ring-0 active:outline-none"
              @click="copyPreset(preset)"
            />
            <AppIcon
              v-tooltip="preset.isOwnPreset ? 'Bewerken' : `Dit profiel is met u gedeeld door ${preset.createdByUser.name} en is daarom niet aanpasbaar.`"
              icon="fa-edit"
              :class="{'text-neutral-400 ml-auto icon !cursor-not-allowed': !preset.isOwnPreset, 'ml-auto hover:text-primary icon active:ring-0 active:outline-none': preset.isOwnPreset}"
              @click="preset.isOwnPreset && editPreset(preset)"
            />
            <AppIcon
              v-tooltip="preset.isOwnPreset ? 'Overschrijven' : `Dit profiel is met u gedeeld door ${preset.createdByUser.name} en is daarom niet aanpasbaar.`"
              icon="fa-floppy-disk-pen"
              :class="{'text-neutral-400 ml-auto icon !cursor-not-allowed': !preset.isOwnPreset, 'ml-auto hover:text-primary icon active:ring-0 active:outline-none': preset.isOwnPreset}"
              @click="preset.isOwnPreset && updateState(preset)"
            />
            <AppIcon
              v-tooltip="defaultPreset == preset.id ? 'Verwijderen als standaard' : 'Instellen als standaard'"
              :icon="defaultPreset == preset.id ? 'fa-house-circle-xmark' : 'fa-house'"
              class="ml-auto hover:text-primary icon active:ring-0 active:outline-none"
              @click="setDefaultPreset(preset)"
            />
            <AppIcon
              v-tooltip="preset.isOwnPreset ? 'Verwijderen' : `Dit profiel is met u gedeeld door ${preset.createdByUser.name} en is daarom niet aanpasbaar.`"
              icon="fa-trash"
              :class="{'text-neutral-400 ml-auto icon !cursor-not-allowed': !preset.isOwnPreset, 'ml-auto hover:text-primary icon active:ring-0 active:outline-none': preset.isOwnPreset}"
              @click="preset.isOwnPreset && deletePreset(preset)"
            />
          </div>
        </div>
        <div v-else-if="isEditing(preset)" class="w-full">
          <AppPresetForm @cancel="stopEditing" @onSave="onSave" :preset="preset"/>
        </div>
      </li>
    </ul>
    <p v-if="presets.length == 0">
      Er zijn nog geen profielen voor dit overzicht.
    </p>
  </div>
</template>

<script setup lang="ts">
import {
	UserGridPreset,
	createGridPreset,
	deleteGridPreset,
	getGridPresets,
	getRoles,
	setDefaultGridPreset,
	updateGridPreset,
	updatePresetState,
} from "@q-food/q-food";
import { IToolPanelParams } from "ag-grid-community";
import { processFilterModel } from "~utils/Grids/GridProcessFilter.ts";
import { ref } from "vue";
import AppPresetForm from "~components/AppPresetForm.vue";
import { translate } from "~/plugins/I18n.ts";
import { useUserStore } from "~stores/User.ts";
import { getFilterModel } from "~utils/Grids/FilterModelHelper.ts";
import {
	GridPresetClipboardPayload,
	parseGridPreset,
	readClipboardText,
	serializeGridPreset,
} from "~utils/Grids/GridPresetClipboard.ts";
import { copyText } from "~utils/platformUtils.ts";
import { useResourceEventBus } from "~utils/useResourceEventBus.ts";

const newPresetName = ref("");
const presets = ref([] as UserGridPreset[]);
const editing = ref<string | null>(null);
const defaultPreset = ref<string>();
const { confirm, error, success, warning } = useToast();

const props = defineProps<{
	params: IToolPanelParams & { gridKey: string };
}>();

const { params } = toRefs(props);
const { activeUser } = storeToRefs(useUserStore());
const { isAllowed } = useUserStore();

const presetBus = useResourceEventBus(`grid-preset:${props.params.gridKey}`);

onMounted(async () => {
	try {
		if (params.value.gridKey) {
			presets.value = await getGridPresets(params.value.gridKey);
			applyDefaultPresets();
		}
	} catch (error) {
		//
	}
});

function applyDefaultPresets() {
	for (const preset of presets.value) {
		const presetMap = preset.usersWithDefault.map((it) => it.id);
		if (presetMap.includes(activeUser.value.id)) {
			defaultPreset.value = preset.id;
		}
	}
}

function stopEditing() {
	editing.value = null;
}

async function onSave() {
	editing.value = null;
	if (params.value.gridKey) {
		presets.value = await getGridPresets(params.value.gridKey);
	}
	presetBus.emit("UPDATED");
}

function isEditing(preset: UserGridPreset) {
	return preset.id == editing.value;
}

function sortPresetsByName() {
	presets.value.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

function applyPreset(preset: UserGridPreset) {
	params.value.api.applyColumnState({ state: JSON.parse(preset.columnState), applyOrder: true });
	params.value.api.setFilterModel(processFilterModel(JSON.parse(preset.filterState)));
}

async function editPreset(preset: UserGridPreset) {
	editing.value = preset.id;
}

async function setDefaultPreset(preset: UserGridPreset) {
	await setDefaultGridPreset(preset.id).then(() => {
		if (defaultPreset.value == preset.id) {
			defaultPreset.value = null;
		} else {
			defaultPreset.value = preset.id;
		}
	});
}

async function updateState(preset: UserGridPreset) {
	await updatePresetState(preset.id, {
		columnState: JSON.stringify(params.value?.api.getColumnState()),
		filterState: getFilterModel(params.value?.api),
	}).then((response) => {
		const presetToUpdate = presets.value.find((obj) => obj.id === response.id);
		if (presetToUpdate) {
			presetToUpdate.filterState = response.filterState;
			presetToUpdate.columnState = response.columnState;
		}
		presetBus.emit("UPDATED", response);
		success("Profiel succesvol aangepast!");
	});
}

async function deletePreset(preset: UserGridPreset) {
	await deleteGridPreset(preset.id);

	const index = presets.value.indexOf(preset);
	presets.value.splice(index, 1);
	presetBus.emit("DELETED", preset);
}

async function saveCurrentStateAsPreset() {
	if (newPresetName.value.trim() == "") return;

	const newPreset = await createGridPreset({
		gridKey: params.value.gridKey,
		name: newPresetName.value.trim(),
		columnState: JSON.stringify(params.value?.api.getColumnState()),
		filterState: getFilterModel(params.value?.api),
	});
	newPresetName.value = "";
	presets.value.push(newPreset);
	sortPresetsByName();
	presetBus.emit("CREATED", newPreset);
}

async function copyPreset(preset: UserGridPreset) {
	try {
		await copyText(serializeGridPreset(preset));
		success(translate("common.messages.Profile copied to clipboard"));
	} catch {
		error(translate("common.messages.Copying failed"));
	}
}

/**
 * Manual fallback for browsers that do not hand page scripts the clipboard contents (Firefox, and
 * anything where the read permission was denied): let the user paste into a textarea themselves.
 */
async function promptForPastedText(): Promise<string | null> {
	const result = await confirm(translate("common.messages.Paste the copied profile below"), {
		icon: undefined,
		input: "textarea",
		inputAttributes: { rows: "6" },
		showDenyButton: false,
		showCancelButton: true,
		confirmButtonText: translate("common.words.continue"),
		cancelButtonText: translate("common.words.cancel"),
	});

	return result.isConfirmed ? (result.value as string) : null;
}

/**
 * The backend has no unique constraint on (user, gridKey, name), so pasting the same profile twice
 * would otherwise produce two indistinguishable rows.
 */
function uniquePresetName(name: string): string {
	const taken = (candidate: string) => presets.value.some((preset) => preset.name === candidate);

	if (!taken(name)) return name;

	let suffix = 1;
	let candidate = `${name} (kopie)`;
	while (taken(candidate)) {
		suffix++;
		candidate = `${name} (kopie ${suffix})`;
	}

	return candidate;
}

/**
 * Keeps only the role ids that exist for the pasting user, warning about the rest. Users without
 * `read role` can only be checked against their own roles — the same fallback AppPresetForm uses.
 */
async function resolveAllowedRoleIds(payload: GridPresetClipboardPayload): Promise<string[]> {
	if (payload.allowedRoles.length == 0) return [];

	const knownIds = isAllowed("read role")
		? ((await getRoles(undefined, 0, 500)).content ?? []).map((role) => role.id)
		: (activeUser.value?.roles ?? []).map((role) => role.id);

	const resolved = payload.allowedRoles.filter((role) => knownIds.includes(role.id));
	const dropped = payload.allowedRoles.filter((role) => !knownIds.includes(role.id));

	if (dropped.length > 0) {
		warning(
			translate("common.messages.These roles do not exist here and were skipped: {0}", [
				dropped.map((role) => role.name).join(", "),
			]),
		);
	}

	return resolved.map((role) => role.id);
}

async function pastePreset() {
	try {
		const text = (await readClipboardText()) ?? (await promptForPastedText());
		if (text == null) return;

		const payload = parseGridPreset(text);
		if (payload == null) {
			error(translate("common.messages.The clipboard does not contain a valid profile."));
			return;
		}

		if (payload.gridKey !== params.value.gridKey) {
			error(
				translate("common.messages.This profile belongs to another overview ({0}) and cannot be pasted here.", [
					payload.gridKey,
				]),
			);
			return;
		}

		const allowedRoleIds = await resolveAllowedRoleIds(payload);

		// A shared profile whose roles all vanished would land as "shared with no role restriction",
		// which the backend reads as visible to everyone - the opposite of what was copied.
		let isShared = payload.isShared;
		if (isShared && payload.allowedRoles.length > 0 && allowedRoleIds.length == 0) {
			isShared = false;
			warning(translate("common.messages.Profile was pasted as private because none of its shared roles exist."));
		}

		const data = {
			gridKey: params.value.gridKey,
			name: uniquePresetName(payload.name),
			columnState: payload.columnState,
			filterState: payload.filterState,
			isShared,
			allowedRoleIds,
		};

		const created = await createGridPreset(data);
		// CreateOrUpdateGridFilterPresetAction only applies allowedRoles on its update branch, so a
		// create silently drops them; a follow-up update is what actually persists the roles.
		const newPreset = allowedRoleIds.length > 0 ? await updateGridPreset(created.id, data) : created;

		presets.value.push(newPreset);
		sortPresetsByName();
		presetBus.emit("CREATED", newPreset);
		success(translate("common.messages.Profile pasted"));
	} catch (e) {
		error((e as Error)?.message ?? translate("common.messages.Something went wrong"));
	}
}
</script>

<style scoped>
.icon {
  @apply p-3 h-4 w-4 cursor-pointer;
}
</style>
